import { afterNextRender, ApplicationRef, ChangeDetectionStrategy, Component, createComponent, ElementRef, EnvironmentInjector, HostListener, Injector, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CitationPanelService } from 'src/app/services/citation-panel.service';
import { isPlainLeftClick } from 'src/app/shared/utils/citation-click.util';
import { CitationNumbering, EMPTY_CITATION_NUMBERING, markersForNumbers, PROSE_CITATION_OVERFLOW_SLOT_CLASS, readProseOverflowNumbers, resolveProseCitations } from 'src/app/shared/utils/citation-resolver.util';
import { CitationOverflowComponent } from '../citation-overflow/citation-overflow.component';
import { MountedOverflow } from './answer-renderer.model';
import { splitAnalystAnswerDetail } from './split-analyst-answer-detail.util';
import { stripAnalystAnswerImages } from './strip-analyst-answer-images.util';

const CITATION_MARKER_LINK_SELECTOR = 'a.citation-marker-link';
const CITATION_MARKER_INDEX_PATTERN = /^\[(\d+)\]$/;
const CITATION_OVERFLOW_SLOT_SELECTOR = `span.${PROSE_CITATION_OVERFLOW_SLOT_CLASS}`;

@Component({
  standalone: false,
  selector: 'app-answer-renderer',
  templateUrl: './answer-renderer.component.html',
  styleUrls: ['./answer-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnswerRendererComponent implements OnChanges, OnDestroy {
  @Input() content = '';
  @Input() citationNumbering: CitationNumbering = EMPTY_CITATION_NUMBERING;
  @Input() isReport = false;

  headlineContent = '';
  detailContent: string | null = null;

  /** Deliberately never reset in `ngOnChanges`: each message keeps one stable component instance, so the
   * field default already gives "closed by default", and a reset would re-collapse an answer the user had
   * opened whenever `citationNumbering` arrives after `content`. */
  expanded = false;

  private mountedOverflows: MountedOverflow[] = [];

  constructor(
    private readonly router: Router,
    private readonly citationPanelService: CitationPanelService,
    private readonly appRef: ApplicationRef,
    private readonly environmentInjector: EnvironmentInjector,
    private readonly injector: Injector,
    private readonly elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content'] || changes['citationNumbering'] || changes['isReport']) {
      const renderedContent = resolveProseCitations(stripAnalystAnswerImages(this.content), this.citationNumbering);
      const { headline, detail } = splitAnalystAnswerDetail(renderedContent, {
        anchorOnOpeningSection: this.isReport,
      });
      this.headlineContent = headline;
      this.detailContent = detail;
    }
    if (changes['citationNumbering']) {
      this.mountedOverflows.forEach((mounted) => this.applyOverflowMarkers(mounted));
    }
  }

  ngOnDestroy(): void {
    this.mountedOverflows.forEach(({ ref }) => ref.destroy());
    this.mountedOverflows = [];
  }

  onMarkdownReady(): void {
    this.destroyDetachedOverflows();
    const mountedHosts = new Set(this.mountedOverflows.map(({ host }) => host));
    this.elementRef.nativeElement.querySelectorAll<HTMLElement>(CITATION_OVERFLOW_SLOT_SELECTOR).forEach((slot) => {
      if (!mountedHosts.has(slot)) {
        this.mountOverflow(slot);
      }
    });
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (!this.expanded) {
      afterNextRender(() => this.destroyDetachedOverflows(), { injector: this.injector });
    }
  }

  /** Citation markers are injected as raw HTML into `<markdown>`, so `routerLink` never compiles onto
   * them; intercept clicks here and open the citation panel ourselves instead of navigating away from
   * the chat. A modified click (ctrl/cmd/shift/alt, or a non-primary button) is left alone so the
   * marker's real `href` still opens the record in a new tab like any other link.
   *
   * The clicked citation is identified from the marker's own rendered text, not from a `data-*`
   * attribute: Angular's `DomSanitizer` has no `data-*` wildcard, so any such attribute is stripped
   * before this handler ever sees it. `resolveProseCitations` writes each marker's number from the message
   * numbering, and `citationNumbering.sources[N - 1]` is that marker's citation regardless of how many
   * times its id repeats - matching by DOM position or by `href` would both resolve a repeated or
   * route-sharing citation to the wrong record. Falls back to navigating the current tab if the text
   * doesn't parse or the index is out of range - defensive only, shouldn't happen since the text came
   * from the same message numbering. */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    const link = (event.target as HTMLElement).closest(CITATION_MARKER_LINK_SELECTOR);
    if (!link || !isPlainLeftClick(event)) {
      return;
    }
    const route = link.getAttribute('href');
    if (!route) {
      return;
    }
    event.preventDefault();
    const match = CITATION_MARKER_INDEX_PATTERN.exec((link.textContent ?? '').trim());
    const index = match ? Number(match[1]) : NaN;
    const citation = index >= 1 ? this.citationNumbering.sources[index - 1] : undefined;
    if (citation) {
      this.citationPanelService.openMarker(citation);
      return;
    }
    void this.router.navigateByUrl(route);
  }

  private mountOverflow(slot: HTMLElement): void {
    const numbers = readProseOverflowNumbers(slot.textContent);
    const ref = createComponent(CitationOverflowComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector,
      hostElement: slot,
    });
    const mounted: MountedOverflow = { ref, host: slot, numbers };
    this.applyOverflowMarkers(mounted);
    this.appRef.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    this.mountedOverflows.push(mounted);
  }

  private destroyDetachedOverflows(): void {
    this.mountedOverflows = this.mountedOverflows.filter(({ ref, host }) => {
      if (!host.isConnected) {
        ref.destroy();
      }
      return host.isConnected;
    });
  }

  private applyOverflowMarkers({ ref, numbers }: MountedOverflow): void {
    ref.setInput('markers', markersForNumbers(numbers, this.citationNumbering));
  }
}
